import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { Event } from '../../../model/event';
import EventStatusDetailsList from './EventStatusDetailsList';
import { Button } from '@mui/material';
import styles from './EventStatus.module.css';
import { CameraUploadDetails, EventUploadDetails } from '../../../model/event-upload-details';
import { Camera } from '../../../model/camera';
import {
  COMPLETE_STATUS,
  FAILED_STATUS,
  FAILED_TO_UPLOAD_STATUS,
  NOT_STARTED_STATUS,
  IN_PROGRESS_STATUS,
  ERROR,
} from './EventStatusConstant';
import { formattedZoneTime } from '../../../app/helper/dateFormatHelper';
import { red } from '@mui/material/colors';
import { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import { getQuarantineImageCountOfEvent } from '../../../api/eventApi';
import QuarantineMessage from './QuarantineMessage';

const DAY = 'Day ';

interface IProps {
  eventDetails: Event;
  canCompleteEvent: boolean;
  completeEvent: Function;
  cancelEventComplete: Function;
  onTimerEnd: (eventId: string) => void;
}

function EventStatusCardContainer({
  eventDetails,
  canCompleteEvent,
  completeEvent,
  cancelEventComplete,
  onTimerEnd,
}: IProps) {
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const [quarantineImageCount, setQuarantineImageCount] = useState<number>(0);
  const onTimerEndRef = useRef(onTimerEnd);

  // Time in seconds before auto-completion is complete after the timer
  const THRESHOLD_TIME_FOR_EVENT_COMPLETION = 90;

  // Keep the ref updated with the latest onTimerEnd prop
  useEffect(() => {
    onTimerEndRef.current = onTimerEnd;
  }, [onTimerEnd]);

  useEffect(() => {
    if (eventDetails.hasQuarantineImages && eventDetails.eventId) {
      getQuarantineImageCountOfEvent(eventDetails.eventId).then((count) => {
        setQuarantineImageCount(count);
      });
    }
  }, [eventDetails.hasQuarantineImages, eventDetails.eventId]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    if (
      eventDetails.status === 'PENDING_EVENT_COMPLETION' &&
      eventDetails.remainingTimeForEventCompletionTimerInSeconds !== undefined &&
      eventDetails.eventId
    ) {
      const endTime = Date.now() + eventDetails.remainingTimeForEventCompletionTimerInSeconds * 1000;
      let diffInSeconds = 0;
      const updateTime = () => {
        diffInSeconds = Math.floor((endTime - Date.now()) / 1000);
        const clampedRemaining = Math.max(0, diffInSeconds);

        setRemainingTime(clampedRemaining);

        if (diffInSeconds <= -THRESHOLD_TIME_FOR_EVENT_COMPLETION && !!intervalId && !!eventDetails.eventId) {
          clearInterval(intervalId);
          onTimerEndRef.current(eventDetails.eventId);
        }
      };

      updateTime();
      const intervalTime = diffInSeconds <= -THRESHOLD_TIME_FOR_EVENT_COMPLETION ? 60000 : 1000;
      intervalId = setInterval(updateTime, intervalTime);
    } else {
      setRemainingTime(null);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [eventDetails.status, eventDetails.remainingTimeForEventCompletionTimerInSeconds, eventDetails.eventId]);
  const formatTime = (timeInSeconds: number | null): string => {
    if (timeInSeconds === null) return '';
    if (timeInSeconds <= 0) return '0'; // Show just "0" if timer has ended
    const minutes = Math.floor(timeInSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (timeInSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const eventStatusDetailsList =
    eventDetails?.days?.map((eventDay, index) => {
      const eventStatusDetailsResult = fetchCameraDetails(eventDay?.cameras);
      eventStatusDetailsResult.day = DAY + (index + 1);
      return eventStatusDetailsResult;
    }) || [];
  const groupPicturesExpected = !!eventDetails?.groupPicturesExpected;

  // Will check whether we have any pending picture day
  // Then add to eventStatusDetailsList for displaying extra days in Status Box
  fetchEventDaysPending(eventDetails, eventStatusDetailsList);
  const zonedDateTime = formattedZoneTime(eventDetails.initiatedDate);

  //if all the days are in NOT_STARTED_STATUS then the event cannot be completed
  const currentPictureDayNotComplete = () => {
    let allPictureDayNotStarted = eventStatusDetailsList.every(
      (eventStatusDetails) => eventStatusDetails.pictureDayStatus === NOT_STARTED_STATUS
    );

    if (!allPictureDayNotStarted) {
      let allComplete = eventStatusDetailsList.every((eventStatusDetails, index) => {
        // If all the camera day completes but last camera days not started then also we can complete the event
        if (!!eventDetails && !!eventDetails.days && index >= eventDetails.days.length) {
          return (
            eventStatusDetails.pictureDayStatus === COMPLETE_STATUS ||
            eventStatusDetails.pictureDayStatus === NOT_STARTED_STATUS
          );
        }
        return eventStatusDetails.pictureDayStatus === COMPLETE_STATUS;
      });
      return !allComplete;
    }
    return allPictureDayNotStarted;
  };

  const handleCompleteEvent = () => {
    if (canCompleteEvent) {
      if (eventDetails.webLoaderUpload) {
        completeEvent(null, 'WEB_UPLOADER_COMPLETE');
      } else {
        completeEvent(null, 'COMPLETION_REQUEST');
      }
    }
  };

  const handleCompleteEventWithoutTimer = () => {
    if (canCompleteEvent) {
      completeEvent(null, 'USER_FORCE_COMPLETE');
    }
  };

  const handleCancelEventCompletion = () => {
    if (canCompleteEvent) {
      cancelEventComplete();
    }
  };

  return (
    <>
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ backgroundColor: 'background.default', p: 2, textAlign: 'left' }}>
          <Typography variant="h6" color="primary.dark">
            Status
          </Typography>
        </CardContent>
        <CardContent>
          {!!eventStatusDetailsList && (
            <EventStatusDetailsList
              eventUploadDetailsList={eventStatusDetailsList}
              groupPicturesExpected={groupPicturesExpected}
            />
          )}
        </CardContent>
        <CardContent>
          {eventDetails.status === 'COMPLETE' ? (
            <Button
              variant="outlined"
              fullWidth
              size="large"
              disableRipple
              className={styles.completeButton}
              sx={{ cursor: 'default' }}
            >
              Event Completed
            </Button>
          ) : eventDetails.status === 'PENDING_EVENT_COMPLETION' ? (
            <>
              <Button
                variant="contained"
                disabled
                fullWidth
                size="large"
                className={styles.pendingCompletionButton}
                sx={{
                  backgroundColor: 'primary.main',
                  color: '#fff',
                  '&.Mui-disabled': {
                    backgroundColor: 'primary.main',
                    color: '#fff',
                  },
                  whiteSpace: 'nowrap',
                }}
              >
                Pending Event Complete
              </Button>
              {remainingTime !== null && (
                <Typography variant="body2" sx={{ marginTop: 1, textAlign: 'center' }}>
                  Event will automatically complete in{' '}
                  <Typography component="span" variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    {formatTime(remainingTime)}
                  </Typography>
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: 2, gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={handleCompleteEventWithoutTimer}
                  disabled={remainingTime === null || remainingTime <= 0}
                >
                  Complete Now
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={handleCancelEventCompletion}
                  sx={{ whiteSpace: 'nowrap' }}
                  disabled={remainingTime === null || remainingTime <= 0}
                >
                  Cancel Event Completion
                </Button>
              </Box>
            </>
          ) : (
            <Button
              variant="contained"
              disabled={currentPictureDayNotComplete() || !canCompleteEvent}
              fullWidth
              size="large"
              className={styles.completeButton}
              onClick={handleCompleteEvent}
            >
              Complete This Event
            </Button>
          )}

          {(eventDetails.status === 'COMPLETE' || eventDetails.status === 'INITIATED') &&
            eventDetails.initiatedDate &&
            zonedDateTime && (
              <Typography variant="body1" sx={{ fontWeight: 500, marginTop: 3 }}>
                Event Completed on <br />
                {zonedDateTime} <br />
                {!!eventDetails.completedBy && ` and Completed by ${eventDetails.completedBy}`} <br />
                {!!eventDetails.isOverrideCompleted && `Override: ${eventDetails.isOverrideCompleted}`}
              </Typography>
            )}
        </CardContent>
      </Card>
      {eventDetails.hasQuarantineImages && (
        <QuarantineMessage imageCount={quarantineImageCount} />
      )}
    </>
  );
}

function fetchCameraDetails(cameras?: Camera[]): EventUploadDetails {
  const eventStatusDetails = new EventUploadDetails();

  eventStatusDetails.pictureDayStatus = COMPLETE_STATUS;
  eventStatusDetails.cameraUploadDetails = cameras?.map((camera) => {
    const cameraUploadDetails = new CameraUploadDetails();

    cameraUploadDetails.cameraName = camera?.cameraId;

    if (camera.status === 'COMPLETE') {
      cameraUploadDetails.uploadStatus = COMPLETE_STATUS;
    } else if (camera.status === 'PENDING') {
      if (eventStatusDetails.pictureDayStatus !== FAILED_STATUS && eventStatusDetails.pictureDayStatus !== ERROR) {
        eventStatusDetails.pictureDayStatus = IN_PROGRESS_STATUS;
      }
      cameraUploadDetails.uploadStatus = IN_PROGRESS_STATUS;
    } else if (camera.status === 'FAILED') {
      eventStatusDetails.pictureDayStatus = FAILED_STATUS;
      cameraUploadDetails.uploadStatus = FAILED_TO_UPLOAD_STATUS;
    } else if (camera.status === 'ERROR') {
      if (eventStatusDetails.pictureDayStatus !== FAILED_STATUS) {
        eventStatusDetails.pictureDayStatus = ERROR;
      }
      cameraUploadDetails.uploadStatus = ERROR;
      cameraUploadDetails.uploadedAssetCountMismatch = true;
    }
    return cameraUploadDetails;
  });

  return eventStatusDetails;
}

function fetchEventDaysPending(eventDetails: Event, eventStatusDetailsList: EventUploadDetails[]) {
  let pictureDayCount = eventDetails?.pictureDays?.length || 0;
  let daysCount = eventDetails?.days?.length || 0;
  for (let i = daysCount!; i < pictureDayCount!; i++) {
    let eventStatusDetails = new EventUploadDetails();
    eventStatusDetails.day = DAY + (i + 1);
    eventStatusDetails.pictureDayStatus = NOT_STARTED_STATUS;
    eventStatusDetailsList.push(eventStatusDetails);
  }
}

export default EventStatusCardContainer;
